import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getInvoice, getAdBanners } from './api';
import fallbackRes from './data/res';
import { Document, Page, pdfjs } from 'react-pdf';
import { Download, ZoomIn, ZoomOut, FileText, ShoppingBag } from 'lucide-react';
import './InvoiceViewer.css';
import GoogleReviewPopup from './components/GoogleReviewPopup';
import AdSlider from './components/AdSlider';
import { pdfStore } from "./utils/pdfstore"

//locally serve worker 
// pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.mjs';
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.js';

function InvoiceViewer() {
  const { invoiceId } = useParams();
  const [pdfLink, setPdfLink] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfViewerReady, setPdfViewerReady] = useState(false);
  const [zoom, setZoom] = useState(1.0); // 100% = fit-to-width baseline
  const [showReviewPopup, setShowReviewPopup] = useState(false);
  const [reviewUrl, setReviewUrl] = useState(null);
  const [adImage, setAdImage] = useState(
    "https://dummyimage.com/468x60/f3f4f6/6b7280&text=Advertisement"
  ); // fallback
  const [adBanners, setAdBanners] = useState([]);
  const viewerRef = useRef(null);
  const [fitWidth, setFitWidth] = useState(null);
  const [isFromCache, setIsFromCache] = useState(false);
  const [fallbackAttempts, setFallbackAttempts] = useState(0);
  const [invoiceNumber, setInvoiceNumber] = useState(null);
  const [logoUrl, setLogoUrl] = useState(''); 
  const [currentInvoiceData, setCurrentInvoiceData] = useState(null);
  
  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        setLoading(true);
        const cached = pdfStore.getInvoicePdf(invoiceId);
        let invoiceData = null;
        let pdfUrl = null;

        if (cached?.url) {
          console.log("[v0] Loading PDF from cache");
          setPdfLink(cached.url);
          pdfUrl = cached.url;
          setIsFromCache(true);
          setInvoiceNumber(cached.metadata?.invoiceNumber);
          setLogoUrl(cached.metadata?.logoUrl);
          setCurrentInvoiceData(cached.metadata);
          if (cached.metadata) {
            invoiceData = {
              shopId: cached.metadata.companyId,
              company: {
                google_review_id: cached.metadata.googleReviewId,
                logo_url: cached.metadata.logoUrl
              },
              ad_image_url: cached.metadata.adImageUrl
            };
          }
        } else {
          console.log("[v0] Fetching invoice from API");
          invoiceData = await getInvoice(invoiceId);
          if (invoiceData?.pdf_link) {
            setPdfLink(invoiceData.pdf_link);
            pdfUrl = invoiceData.pdf_link;
            setIsFromCache(false);
            setInvoiceNumber(invoiceData.invoiceId);
            setLogoUrl(invoiceData.company?.logo_url);
            setCurrentInvoiceData(invoiceData);
            
          } else {
            console.warn("No PDF link found, using fallback.");
            setPdfLink(fallbackRes.pdfUrl);
            setError("No PDF link found, using fallback.");
            setIsFromCache(false);
            setInvoiceNumber(invoiceId);
            setLogoUrl(logoUrl);
          }
        }

        if (invoiceData) {
          if (invoiceData.shopId) {
            try {
              console.log("[v0] Fetching ad banners from API");
              const banners = await getAdBanners(invoiceData.shopId);
              if (banners && banners.length > 0) {
                const activeBanners = banners.filter(
                  (b) => b.status === "Active"
                );
                setAdBanners(activeBanners);
                setAdImage(banners[0].image_url);
              }
            } catch (err) {
              console.warn("Failed to load ad banners");
              setAdBanners([]);
            }
          }

          // Google Review logic
          const googleReviewId = invoiceData?.company?.google_review_id;
          if (googleReviewId) {
            const reviewUrl = `https://g.page/r/${googleReviewId}/review`;
            setReviewUrl(reviewUrl);
            setShowReviewPopup(true);
          } else {
            setReviewUrl(null);
            setShowReviewPopup(false);
          }

          // Ad logic
          if (invoiceData?.ad_image_url) {
            setAdImage(invoiceData.ad_image_url);
          } else if (!adBanners.length) {
            setAdImage(null);
          }
        }
      } catch (err) {
        console.error(err);
        setPdfLink(fallbackRes.pdfUrl);
        setError("Failed to load invoice from API, fallback used.");
        setReviewUrl(null);
        setShowReviewPopup(false);
        setAdImage(
          "https://dummyimage.com/468x60/f3f4f6/6b7280&text=Advertisement"
        );
        setIsFromCache(false);
        setInvoiceNumber(invoiceId);
        setLogoUrl(logoUrl);
      } finally {
        setLoading(false);
      }
    };
    fetchInvoice();
  }, [invoiceId]);

  // Measure available width and keep pages fit-to-screen by default
  useEffect(() => {
    const measureFitWidth = () => {
      const el = viewerRef.current;
      const rectWidth = el?.getBoundingClientRect
        ? el.getBoundingClientRect().width
        : null;
      const width = Math.max(320, Math.floor(rectWidth || window.innerWidth));
      setFitWidth(width);
    };

    measureFitWidth();

    let ro;
    if (window.ResizeObserver) {
      ro = new ResizeObserver(() => measureFitWidth());
      if (viewerRef.current) ro.observe(viewerRef.current);
    }
    window.addEventListener("resize", measureFitWidth);
    window.addEventListener("orientationchange", measureFitWidth);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", measureFitWidth);
      window.removeEventListener("orientationchange", measureFitWidth);
    };
  }, []);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPdfLoading(false);
    setPdfViewerReady(true);
    setError(null);
    setFallbackAttempts(0);

    if (!isFromCache && pdfLink && currentInvoiceData) {
      console.log("[v0] Caching successfully loaded PDF with metadata");
      pdfStore.storeInvoicePdf(invoiceId, pdfLink, currentInvoiceData)
        .catch((err) => {
          console.warn("Failed to cache PDF:", err);
        });
    }
  }

  const onDocumentLoadError = (err) => {
    console.error("PDF Load Error:", err);
    setError("Failed to load PDF: " + err.message);
    setPdfLoading(false);
    setPdfViewerReady(false); 

    if (isFromCache) {
      console.log("[v0] Clearing failed cached PDF");
      pdfStore.clearCache(invoiceId);
    }
  };

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.2, 3));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.2, 0.6));

  const handleDownload = () => {
    if (pdfLink) {
      const link = document.createElement("a");
      link.href = pdfLink;
      link.download = `invoice-${invoiceId || "download"}.pdf`;
      link.click();
    }
  };

  const renderDownloadFallback = () => (
    <div className="fallback-container">
      <div className="fallback-content">
        <ShoppingBag size={64} className="fallback-icon" />
        <h2 className="fallback-title">Thank you for shopping with us 🛍️</h2>
        <p className="fallback-subtitle">Your invoice is ready for download</p>
        <button className="fallback-download-btn" onClick={handleDownload}>
          <Download size={20} />
          Download Invoice PDF
        </button>
        <p className="fallback-note">Invoice #{invoiceNumber} • PDF Document</p>
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className="container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p className="loading-text">
            {isFromCache ? "Loading url..." : "Loading PDF URL..."}
          </p>
        </div>
      </div>
    );
  }

  if (!pdfLink) {
    return (
      <div className="container">
        <GoogleReviewPopup 
          isOpen={showReviewPopup} 
          onClose={() => setShowReviewPopup(false)} 
          reviewUrl={reviewUrl} 
        />
        <div className="error-container">
          <FileText size={48} color="#6b7280" />
          <p className="error-text">No PDF URL available</p>
        </div>
      </div>
    )
  }

  if (!pdfViewerReady || pdfLoading) {
    return (
      <div className="container" style={{ paddingBottom: "10vh" }}>
        <GoogleReviewPopup
          isOpen={showReviewPopup}
          onClose={() => setShowReviewPopup(false)}
          reviewUrl={reviewUrl}
        />
        
        {renderDownloadFallback()}
        
        <div style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}>
          <Document
            file={pdfLink}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={null}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingBottom: "10vh" }}>
      <GoogleReviewPopup
        isOpen={showReviewPopup}
        onClose={() => setShowReviewPopup(false)}
        reviewUrl={reviewUrl}
      />
      {/* PDF Toolbar */}
      <div className="toolbar">
        <div className="toolbar-left">
          <div className="logo-section">
            <img 
              src={logoUrl} 
              alt="Business Logo" 
              className="business-logo"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <div className="invoice-info">
              <span className="document-title">
                Invoice #{invoiceNumber}
              </span>
            </div>
          </div>
        </div>
        <div className="toolbar-right">
          {numPages && (
            <span className="page-info">
              {numPages} page{numPages > 1 ? "s" : ""}
            </span>
          )}
          <button
            className="toolbar-button"
            onClick={handleZoomOut}
            title="Zoom Out"
          >
            <ZoomOut size={16} />
          </button>
          <span className="zoom-level">{Math.round(zoom * 100)}%</span>
          <button
            className="toolbar-button"
            onClick={handleZoomIn}
            title="Zoom In"
          >
            <ZoomIn size={16} />
          </button>
          <div className="divider"></div>
          <button
            className="download-button"
            onClick={handleDownload}
            title="Download PDF"
          >
            <Download size={16} />
            <span className="download-text">Download</span>
          </button>
        </div>
      </div>

      {/* PDF Viewer */}
      <div className="viewer-wrapper" ref={viewerRef}>
        <Document
          file={pdfLink}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={
            <div className="loading-container">
              <div className="spinner"></div>
              <p className="loading-text">Loading PDF Document...</p>
            </div>
          }
          error={renderDownloadFallback()}
        >
          {!pdfLoading && numPages && (
            <div className="pages-container">
              {Array.from(new Array(numPages), (_, index) => (
                <div key={`page_${index + 1}`} className="page-wrapper">
                  <Page
                    pageNumber={index + 1}
                    width={fitWidth ? Math.floor(fitWidth * zoom) : undefined}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                  />
                </div>
              ))}
            </div>
          )}
        </Document>
      </div>

      {/* <div className="pdfFooter">
        <div className="pdfFooterInner">
          <AdSlider
            banners={[
              ...(Array.isArray(adBanners)
                ? adBanners.filter((b) => b.status === "Active")
                : []),
              ...(adImage ? [{ image_url: adImage, status: "Active" }] : []),
            ].filter((b) => b && b.image_url && b.status === "Active")}
            height="100%"
            fit="cover"
            autoplay
            interval={5000}
            rounded
            showArrows
            showDots
            invoiceNumber={invoiceId}
          />
        </div>
        </div> */}
    </div>
  );
}

export default InvoiceViewer;
